import { useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useRef, Fragment } from 'react';
import { useCookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';

import { getPreviewsAdmin } from 'store/actions/admin';
import { FovLoader, FovOption, FovSelect, SearchInputField, VerticalSpacer } from 'components';
import { MemberContainer } from 'pages/ArchVisionAdminDashboard/styles';
import {
    FilterContainer,
    FilterGroup,
    FilterLabel,
    FilterSelectContainer,
    JobsHeaderContainer,
    JobTypeSelectContainer,
    RefreshButton,
    TableArea,
    TableFooter,
    ResultSummary,
    RowsPerPage,
    RowsPerPageSelect,
    EmptyJobsState,
    EmptyStateTitle,
    EmptyStateText,
    ClearFilterButton,
} from '../Jobs/styles';
import { listColumns } from './listColumns';

const FILTER_PARAMS = [ 'search', 'job_type', 'client_id' ];

const CLIENT_LABELS = {
    sW6TUDnmIHCybCl7l5KMu27QFmBmBAFh0TFsy5FJ: 'AVAIL',
};

const createdOptions = [
    { value: 'CREATED_NEW_TO_OLD', label: 'Newest first' },
    { value: 'CREATED_OLD_TO_NEW', label: 'Oldest first' },
];

const AdminPreviews = () => {
    const dispatch = useDispatch();

    const userDataStore = useSelector(state => state.userData);
    const adminDataStore = useSelector(state => state.adminData);
    const searchFieldRef = useRef('');

    const [ cookies ] = useCookies([ 'userAuth' ]);
    const token = cookies.archvision_token;

    const selectedTheme = userDataStore.userPreferences.theme;
    const previews = useMemo(() => adminDataStore.previews, [ adminDataStore.previews ]);
    const previewTypes = useMemo(() => adminDataStore.previewTypes ?? [], [ adminDataStore.previewTypes ]);
    const previewClients = useMemo(
        () => (adminDataStore.previewClients ?? []).filter(item => item.client_id),
        [ adminDataStore.previewClients ],
    );
    const isLoading = useMemo(() => adminDataStore.isLoading, [ adminDataStore.isLoading ]);
    const pagination = useMemo(() => adminDataStore.pagination, [ adminDataStore.pagination ]);
    const { page, pageSize, totalCount } = pagination;

    const [ searchParams, setSearchParams ] = useSearchParams();

    const handlePageChange = page => {
        if (Number(searchParams.get('page')) === page) return;

        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('page', page);
        setSearchParams(nextSearchParams);
    };

    const handleFilterChange = (parameter, value) => {
        const nextSearchParams = new URLSearchParams(searchParams);

        if (value === '') {
            nextSearchParams.delete(parameter);
        } else {
            nextSearchParams.set(parameter, value);
        }

        nextSearchParams.set('page', 1);
        setSearchParams(nextSearchParams);
    };

    const handleBlur = () => handleFilterChange('search', searchFieldRef.current.value);

    const handleClearFilters = () => {
        const nextSearchParams = new URLSearchParams(searchParams);

        FILTER_PARAMS.forEach(parameter => nextSearchParams.delete(parameter));
        nextSearchParams.set('page', 1);

        if (searchFieldRef.current) {
            searchFieldRef.current.value = '';
        }

        setSearchParams(nextSearchParams);
    };

    const hasActiveFilters = FILTER_PARAMS.some(parameter => searchParams.has(parameter));

    const handlePageSizeChange = value => {
        const nextSearchParams = new URLSearchParams(searchParams);

        nextSearchParams.set('per_page', value);
        nextSearchParams.set('page', 1);

        setSearchParams(nextSearchParams);
    };

    const queryString = searchParams.toString();
    useEffect(() => {
        if (!token) return;

        dispatch(getPreviewsAdmin(token));
    }, [ dispatch, token, queryString ]);

    return (
        <Fragment>
            <VerticalSpacer size={7} />
            <FovLoader
                text='Retrieving Previews'
                resource={!isLoading}
            >
                <JobsHeaderContainer>
                    <SearchInputField
                        width={300}
                        disabled={false}
                        ref={searchFieldRef}
                        defaultValue={searchFieldRef.current?.value || searchParams.get('search')}
                        onChange={e => searchFieldRef.current.value = e.target.value}
                        onKeyUp={e => e.key === 'Enter' && e.target.blur()}
                        onBlur={handleBlur}
                    />
                    <FilterContainer>
                        <FilterGroup>
                            <FilterLabel $selectedTheme={selectedTheme}>Job Type</FilterLabel>
                            <JobTypeSelectContainer>
                                <FovSelect
                                    value={searchParams.get('job_type') || ''}
                                    onChange={(event, newValue) => {
                                        handleFilterChange('job_type', newValue);
                                    }}
                                >
                                    <FovOption value=''>All</FovOption>

                                    {previewTypes.map(item => (
                                        <FovOption key={item.type} value={item.type}>
                                            {item.type}
                                        </FovOption>
                                    ))}
                                </FovSelect>
                            </JobTypeSelectContainer>
                        </FilterGroup>
                        <FilterGroup>
                            <FilterLabel $selectedTheme={selectedTheme}>Client</FilterLabel>
                            <JobTypeSelectContainer>
                                <FovSelect
                                    value={searchParams.get('client_id') || ''}
                                    onChange={(event, newValue) => {
                                        handleFilterChange('client_id', newValue);
                                    }}
                                >
                                    <FovOption value=''>All</FovOption>

                                    {previewClients.map(item => (
                                        <FovOption key={item.client_id} value={item.client_id}>
                                            {CLIENT_LABELS[item.client_id] ?? item.client_id}
                                        </FovOption>
                                    ))}
                                </FovSelect>
                            </JobTypeSelectContainer>
                        </FilterGroup>
                        <FilterGroup>
                            <FilterLabel $selectedTheme={selectedTheme}>Created</FilterLabel>
                            <FilterSelectContainer>
                                <FovSelect
                                    value={searchParams.get('order_by') || 'CREATED_NEW_TO_OLD'}
                                    onChange={(event, newValue) => {
                                        handleFilterChange('order_by', newValue);
                                    }}
                                >
                                    {createdOptions.map(option => (
                                        <FovOption
                                            key={option.value}
                                            value={option.value}
                                        >{option.label}</FovOption>
                                    ))}
                                </FovSelect>
                            </FilterSelectContainer>
                        </FilterGroup>
                        <RefreshButton
                            $selectedTheme={selectedTheme}
                            type='button'
                            disabled={isLoading}
                            onClick={() => dispatch(getPreviewsAdmin(token))}
                        >
                            <span aria-hidden='true'>↻</span>
                            {isLoading ? 'Refreshing...' : 'Refresh'}
                        </RefreshButton>
                    </FilterContainer>
                </JobsHeaderContainer>

                {previews?.length === 0 ? (
                    <EmptyJobsState $selectedTheme={selectedTheme}>
                        {hasActiveFilters ? (
                            <>
                                <EmptyStateTitle>No Matching Previews</EmptyStateTitle>
                                <EmptyStateText>
                                    Try changing your search or clearing the filters.
                                </EmptyStateText>

                                <ClearFilterButton
                                    $selectedTheme={selectedTheme}
                                    type='button'
                                    onClick={handleClearFilters}
                                >Clear Filters</ClearFilterButton>
                            </>
                        ) : (
                            <>
                                <EmptyStateTitle>No Previews Available</EmptyStateTitle>
                                <EmptyStateText>
                                    Previews will appear here after a preview job publishes successfully.
                                </EmptyStateText>
                            </>
                        )}
                    </EmptyJobsState>
                ) : (
                    <Fragment>
                        <TableArea>
                            <MemberContainer
                                dataSource={previews}
                                $selectedTheme={selectedTheme}
                                columns={listColumns(selectedTheme)}
                                rowKey={'row-' + Math.random()}
                                currentPage={page}
                                pageSize={pageSize}
                                totalCount={totalCount}
                                isPagination={true}
                                selectedKey='job_guid'
                                selectedGuid={null}
                                onPageChange={handlePageChange}
                            />
                        </TableArea>
                        <TableFooter $selectedTheme={selectedTheme}>
                            <ResultSummary $selectedTheme={selectedTheme}>
                                { totalCount === 0
                                    ? 'Showing 0 previews'
                                    : `Showing ${(page - 1) * pageSize + 1} to ${
                                        Math.min(page * pageSize, totalCount)
                                    } of ${totalCount} previews` }
                            </ResultSummary>
                            <RowsPerPage>
                                <FilterLabel $selectedTheme={selectedTheme}>
                                    Rows per page:
                                </FilterLabel>
                                <RowsPerPageSelect>
                                    <FovSelect
                                        value={String(
                                            searchParams.get('per_page') || pageSize || 10,
                                        )}
                                        onChange={(event, newValue) => {
                                            handlePageSizeChange(newValue);
                                        }}
                                    >
                                        <FovOption value='10'>10</FovOption>
                                        <FovOption value='25'>25</FovOption>
                                        <FovOption value='50'>50</FovOption>
                                        <FovOption value='100'>100</FovOption>
                                    </FovSelect>
                                </RowsPerPageSelect>
                            </RowsPerPage>
                        </TableFooter>
                    </Fragment>
                )}
            </FovLoader>
        </Fragment>
    );
};

export default AdminPreviews;
